import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  IconType, ButtonProps, Icon,
} from '@routific/hive';
import { MoveToDateIcon } from '@routific/hive/v5';
import type { V5ButtonHierarchy } from '@routific/hive/v5';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useStore } from '../../../../store/mst';
import ActionButton, { CommonActionTypes, ButtonTypes } from '../../../shared/ActionButton';
import useMoveRoutes from './hooks/useMoveRoutes';
import MoveDateModal from './MoveDateModal';
import { RouteProps } from '../../../../store/mst/utils/getRoutesProps';

interface Props {
  routeUuids: string[]
  type?: ButtonTypes
  buttonProps?: ButtonProps
  disabled?: boolean
  // Render as a v5 Button, for the v5 chrome (the page footer's action band).
  v5?: boolean
  v5Hierarchy?: V5ButtonHierarchy
  routeProps: RouteProps;
}

const MoveButton: React.FC<Props> = ({
  routeUuids,
  type,
  buttonProps,
  disabled,
  routeProps,
  v5,
  v5Hierarchy,
}) => {
  const store = useStore();
  const { t } = useTranslation('components');
  const selectedWorkspaceId = store.ui.selectedWorkspace?.id;
  const { isDispatched } = routeProps;

  // Business logic hook
  const { isSubmitting, moveRoutes } = useMoveRoutes();

  // Local UI state
  const [visible, setVisible] = useState<boolean>(false);
  const [selectedDate, selectDate] = useState<DateTime | undefined>(
    store.ui.selectedDate ? DateTime.fromISO(store.ui.selectedDate) : DateTime.now(),
  );

  // Moving to the date the routes already sit on is a no-op, so block it.
  const isSameAsCurrentDate = !!store.ui.selectedDate
    && !!selectedDate
    && selectedDate.toISODate() === store.ui.selectedDate;

  const onSelectDate = (value: DateTime | null) => {
    selectDate(value ?? undefined);
  };

  const handleCancel = () => {
    setVisible(false);
    selectDate(store.ui.selectedDate ? DateTime.fromISO(store.ui.selectedDate) : DateTime.now());
  };

  const handleSubmit = async () => {
    const targetDate = selectedDate?.toISODate();
    if (!targetDate || !selectedWorkspaceId || isSubmitting || isSameAsCurrentDate) {
      return;
    }

    try {
      await moveRoutes(routeUuids, targetDate);
      setVisible(false);
    } catch {
      // moveRoutes already surfaced the failure via a notification; keep the modal open
      // so the user can retry without losing their selected date.
    }
  };

  const handleClickMove = (e?: any) => {
    e?.stopPropagation?.();
    if (!selectedWorkspaceId) return;
    setVisible(true);
  };

  const getTooltipText = () => {
    if (isDispatched) {
      return t('publish.tooltip.routeDispatched');
    }
    if (disabled) {
      return t('moreActionsButton.route.button.move.tooltip.noPermission');
    }
    if (type === ButtonTypes.BUTTON) {
      return t('moreActionsButton.route.button.move.tooltip.button');
    }
    return undefined;
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <MoveDateModal
        visible={visible}
        selectedDate={selectedDate}
        isSubmitting={isSubmitting}
        routeCount={routeUuids.length}
        disableSubmit={isSameAsCurrentDate}
        onSelectDate={onSelectDate}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
      <ActionButton
        v5={v5}
        v5Hierarchy={v5Hierarchy}
        v5Icon={<MoveToDateIcon size="md" />}
        name={CommonActionTypes.MOVE}
        type={type}
        disabled={disabled}
        tooltipProps={{
          title: getTooltipText(),
        }}
        menuItemText={t('moreActionsButton.route.button.move.text')}
        menuItemProps={{
          onClick: handleClickMove,
          icon: <Icon icon={IconType.MoveToDate} />,
        }}
        buttonProps={{
          icon: IconType.MoveToDate,
          onClick: handleClickMove,
          ...buttonProps,
        }}
      />
    </div>
  );
};

export default observer(MoveButton);
